import React from 'react';
import { StandardSection, createEntitySectionConfig } from '../common';
import type { TableColumn } from '../common';
import type { Trepano } from '../../types';
import { trepanoFormFields } from '../../config/formFields';

interface TrepanosSectionProps {
  data: {
    trepanos: Trepano[];
  };
  operations: {
    handleTrepanoSubmit: (data: Record<string, any>) => Promise<void>;
    handleTrepanoToggle: (item: Trepano) => Promise<void>;
    handleTrepanoDelete: (item: Trepano) => Promise<void>;
    handleTrepanoEdit: (item: Trepano, key: string, newValue: any) => Promise<void>;
  };
  userPermissions: {
    canEditReports: boolean;
    canDeleteReports: boolean;
  };
}

export const TrepanosSection: React.FC<TrepanosSectionProps> = ({ data, operations, userPermissions }) => {
  // Table column configurations - using actual Trepano fields
  const trepanoColumns: TableColumn<Trepano>[] = [
    {
      key: 'nombre',
      title: 'Nombre del Trepano',
      width: '40%',
      editable: true,
      editType: 'text',
      onEdit: operations.handleTrepanoEdit
    },
    {
      key: 'tamanio',
      title: 'Tamaño',
      width: '20%',
      editable: true,
      editType: 'text',
      onEdit: operations.handleTrepanoEdit,
      render: (value) => value ? `${value}"` : 'N/A'
    },
    {
      key: 'activo',
      title: 'Estado',
      width: '20%',
      render: (value) => (
        <span style={{
          color: value ? '#4CAF50' : '#f44336',
          fontWeight: 'bold'
        }}>
          {value ? '✅ Activo' : '❌ Inactivo'}
        </span>
      )
    }
  ];

  // Quick Setup Handler for standard trepanos - using actual field names
  const handleTrepanosQuickSetup = async () => {
    if (window.confirm('¿Cargar trepanos estándar? Esta acción creará trepanos de ejemplo con tamaños comunes.')) {
      const trepanoTemplates = [
        { nombre: 'Trepano PDC 26"', tamanio: '26', activo: true },
        { nombre: 'Trepano Tricónico 17 1/2"', tamanio: '17 1/2', activo: true },
        { nombre: 'Trepano PDC 12 1/4"', tamanio: '12 1/4', activo: true },
        { nombre: 'Trepano PDC 8 1/2"', tamanio: '8 1/2', activo: true },
        { nombre: 'Trepano Diamante 6 1/8"', tamanio: '6 1/8', activo: true },
        { nombre: 'Trepano Bicéntrico 6"', tamanio: '6', activo: true }
      ];

      try {
        for (const template of trepanoTemplates) {
          await operations.handleTrepanoSubmit(template);
        }
        alert('✅ Trepanos estándar cargados exitosamente');
      } catch (error) {
        console.error('Error cargando trepanos:', error);
        alert('❌ Error cargando trepanos. Ver consola para detalles.');
      }
    }
  };

  return (
    <>
      <div style={{ marginBottom: '30px', textAlign: 'center' }}>
        <h2>🔩 Gestión de Trepanos</h2>
        <p style={{ color: '#666', marginTop: '10px' }}>
          Administra los trepanos de perforación con especificaciones técnicas
        </p>
      </div>

      {/* Trepanos Section */}
      <StandardSection
        config={createEntitySectionConfig(
          'Trepanos de Perforación',
          '🔩',
          data.trepanos,
          trepanoFormFields,
          trepanoColumns,
          operations.handleTrepanoSubmit,
          operations.handleTrepanoToggle,
          operations.handleTrepanoDelete,
          {
            description: 'Gestiona los trepanos de perforación con especificaciones técnicas y estado de actividad',
            quickSetup: {
              title: 'Carga Rápida de Trepanos',
              description: 'Crea trepanos de ejemplo con tamaños estándar (PDC, Tricónico, Diamante)',
              buttonLabel: '🔩 Cargar Trepanos Estándar',
              onQuickSetup: handleTrepanosQuickSetup
            }
          }
        )}
      />

      {/* Summary Section */}
      <div style={{
        marginTop: '30px',
        padding: '20px',
        backgroundColor: '#f3e5f5',
        borderRadius: '12px',
        border: '2px solid #9C27B0'
      }}>
        <h3 style={{ color: '#7B1FA2', marginBottom: '15px' }}>📊 Resumen de Trepanos</h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px',
          marginTop: '15px'
        }}>
          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              🔩 Total Trepanos
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#9C27B0'
            }}>
              {data.trepanos.length}
            </div>
          </div>

          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              ✅ Trepanos Activos
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#4CAF50'
            }}>
              {data.trepanos.filter(trepano => trepano.activo).length}
            </div>
          </div>

          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              🔧 Tamaños Únicos
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#FF9800'
            }}>
              {new Set(data.trepanos.map(trepano => trepano.tamanio)).size}
            </div>
          </div>
        </div>

        {/* Lista de Trepanos */}
        {data.trepanos.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <h4 style={{ color: '#7B1FA2', marginBottom: '10px' }}>Lista de Trepanos:</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '10px' }}>
              {data.trepanos.map((trepano, index) => (
                <div key={trepano.id || index} style={{
                  padding: '15px',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  border: '1px solid #ddd',
                  opacity: trepano.activo ? 1 : 0.6
                }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#7B1FA2' }}>
                    🔩 {trepano.nombre}
                  </div>
                  <div style={{ fontSize: '14px', marginBottom: '8px' }}>
                    <strong>Tamaño:</strong> {trepano.tamanio}"
                  </div>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: 'bold',
                    color: trepano.activo ? '#4CAF50' : '#f44336'
                  }}>
                    {trepano.activo ? '✅ Activo' : '❌ Inactivo'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {data.trepanos.length === 0 && (
          <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
            No hay trepanos registrados. Usa la carga rápida para empezar con trepanos estándar.
          </div>
        )}
      </div>
    </>
  );
};